import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { attendeeApi } from '@/api/attendee'
import { Button } from '@/components/ui/button'
import { Check } from 'lucide-react'
import { SelfAttendeeAvatar } from '@/features/photos/PhotoAvatars'
import { useAttendeePhotoSettings } from '@/features/photos/photoSources'

export default function AttendeeProfilePage() {
  const qc = useQueryClient()
  const photoSettings = useAttendeePhotoSettings()
  const { data: profile, isLoading } = useQuery({
    queryKey: ['attendee-me'],
    queryFn: () => attendeeApi.getMe().then(r => r.data),
  })

  const [fullName, setFullName] = useState('')
  const [phone, setPhone] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [marketingConsent, setMarketingConsent] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (profile) {
      setFullName(profile.fullName)
      setPhone(profile.phone ?? '')
      setCompanyName(profile.companyName ?? '')
      setMarketingConsent(profile.marketingConsent)
    }
  }, [profile])

  const save = useMutation({
    mutationFn: () => attendeeApi.updateMe({
      fullName,
      phone: phone || null,
      companyName: companyName || null,
      marketingConsent,
    }),
    onSuccess: () => {
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
      qc.invalidateQueries({ queryKey: ['attendee-me'] })
    },
  })

  if (isLoading || !profile) return <p className="text-sm text-slate-400">Loading…</p>

  const label = 'block text-sm font-medium text-slate-700 mb-1'
  const input = 'w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400'

  return (
    <div className="max-w-md space-y-5">
      <h1 className="text-lg font-semibold text-slate-900">Your details</h1>

      {photoSettings.enabled && (
        <div className="flex items-center gap-4 bg-white border border-slate-200 rounded-xl p-5">
          <SelfAttendeeAvatar profile={profile} size={72} />
          <div>
            <p className="text-sm font-medium text-slate-900">Your photo</p>
            <p className="text-xs text-slate-500 mt-0.5">
              {photoSettings.notice?.trim() || 'Add a photo so anyone checking your certificate can confirm it is really you.'}
            </p>
          </div>
        </div>
      )}

      <form onSubmit={e => { e.preventDefault(); save.mutate() }} className="space-y-4 bg-white border border-slate-200 rounded-xl p-5">
        <div>
          <label className={label}>Full name</label>
          <input className={input} value={fullName} onChange={e => setFullName(e.target.value)} required />
        </div>
        <div>
          <label className={label}>Email</label>
          <input className={`${input} bg-slate-50 text-slate-400`} value={profile.email} disabled />
        </div>
        <div>
          <label className={label}>Phone</label>
          <input className={input} value={phone} onChange={e => setPhone(e.target.value)} placeholder="Optional" />
        </div>
        <div>
          <label className={label}>Company</label>
          <input className={input} value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="Optional" />
        </div>
        <label className="flex items-start gap-2 text-sm text-slate-600 cursor-pointer">
          <input
            type="checkbox"
            checked={marketingConsent}
            onChange={e => setMarketingConsent(e.target.checked)}
            className="mt-0.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-400"
          />
          Email me about future courses and training.
        </label>
        <div className="flex items-center gap-3">
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save changes'}
          </Button>
          {saved && <span className="flex items-center gap-1 text-sm text-green-600"><Check size={15} /> Saved</span>}
        </div>
      </form>
    </div>
  )
}
